import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { DocumentLink } from "@/components/DocumentLink";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Upload, Trash2, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";

interface CourseDocument {
  id: string;
  title: string;
  description: string | null;
  file_path: string;
  file_size: number | null;
  folder_name: string | null;
  folder_display_order: number;
}

interface CourseDocumentsProps {
  courseId: string;
}

export const CourseDocuments = ({ courseId }: CourseDocumentsProps) => {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<CourseDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentForm, setDocumentForm] = useState({
    title: "",
    description: "",
    url: "",
    folderName: "",
  });
  const [existingFolders, setExistingFolders] = useState<string[]>([]);
  const [uploadMethod, setUploadMethod] = useState<"file" | "url">("file");

  useEffect(() => {
    if (courseId) {
      fetchDocuments();
    }
  }, [courseId]);

  const fetchDocuments = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("course_documents")
        .select("*")
        .eq("course_id", courseId)
        .order("folder_display_order", { ascending: true })
        .order("display_order", { ascending: true });

      if (error) throw error;
      
      const docs = data || [];
      setDocuments(docs);
      
      // Extract unique folder names
      const folders = Array.from(
        new Set(docs.map(d => d.folder_name).filter(Boolean))
      ) as string[];
      setExistingFolders(folders);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast({
        title: "Error",
        description: "Failed to load course documents",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== "application/pdf") {
        toast({
          title: "Invalid file type",
          description: "Please select a PDF file",
          variant: "destructive",
        });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Maximum file size is 10MB",
          variant: "destructive",
        });
        return;
      }
      setSelectedFile(file);
      if (!documentForm.title) {
        setDocumentForm({
          ...documentForm,
          title: file.name.replace(".pdf", ""),
        });
      }
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (uploadMethod === "file" && (!selectedFile || !documentForm.title)) {
      toast({
        title: "Missing information",
        description: "Please select a file and provide a title",
        variant: "destructive",
      });
      return;
    }

    if (uploadMethod === "url" && (!documentForm.url || !documentForm.title)) {
      toast({
        title: "Missing information",
        description: "Please provide a URL and title",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploading(true);

      if (uploadMethod === "file" && selectedFile) {
        // Upload file to storage
        const fileExt = selectedFile.name.split(".").pop();
        const fileName = `${courseId}/${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from("course-documents")
          .upload(fileName, selectedFile);

        if (uploadError) throw uploadError;

        // Insert document record
        const { error: insertError } = await supabase
          .from("course_documents")
          .insert({
            course_id: courseId,
            title: documentForm.title,
            description: documentForm.description || null,
            file_path: fileName,
            file_size: selectedFile.size,
            folder_name: documentForm.folderName || null,
            folder_display_order: 0,
          });

        if (insertError) throw insertError;
      } else {
        // Insert document record with URL
        const { error: insertError } = await supabase
          .from("course_documents")
          .insert({
            course_id: courseId,
            title: documentForm.title,
            description: documentForm.description || null,
            file_path: documentForm.url,
            file_size: null,
            folder_name: documentForm.folderName || null,
            folder_display_order: 0,
          });

        if (insertError) throw insertError;
      }

      toast({
        title: "Success",
        description: "Document added successfully",
      });

      setIsDialogOpen(false);
      setSelectedFile(null);
      setDocumentForm({ title: "", description: "", url: "", folderName: "" });
      fetchDocuments();
    } catch (error) {
      console.error("Error uploading document:", error);
      toast({
        title: "Error",
        description: "Failed to add document",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (documentId: string, filePath: string) => {
    if (!confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("course-documents")
        .remove([filePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from("course_documents")
        .delete()
        .eq("id", documentId);

      if (dbError) throw dbError;

      toast({
        title: "Success",
        description: "Document deleted successfully",
      });

      fetchDocuments();
    } catch (error) {
      console.error("Error deleting document:", error);
      toast({
        title: "Error",
        description: "Failed to delete document",
        variant: "destructive",
      });
    }
  };

const getDocumentUrl = async (filePath: string) => {
    const { data, error } = await supabase.storage
      .from("course-documents")
      .createSignedUrl(filePath, 3600); // 1 hour expiry
    return data?.signedUrl || '';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Course Documents
          </CardTitle>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2">
                <Upload className="h-4 w-4" />
                Upload Document
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh]">
              <DialogHeader>
                <DialogTitle>Add Course Document</DialogTitle>
                <DialogDescription>
                  Upload a PDF file or link to an external document.
                </DialogDescription>
              </DialogHeader>
              <ScrollArea className="max-h-[60vh] pr-4">
                <form onSubmit={handleUpload} className="space-y-4">
                  <Tabs value={uploadMethod} onValueChange={(v) => setUploadMethod(v as "file" | "url")}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="file">Upload File</TabsTrigger>
                      <TabsTrigger value="url">Link External</TabsTrigger>
                    </TabsList>
                    <TabsContent value="file" className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="file">PDF File *</Label>
                        <Input
                          id="file"
                          type="file"
                          accept=".pdf"
                          onChange={handleFileChange}
                          required={uploadMethod === "file"}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Maximum file size is 10MB</p>
                      </div>
                    </TabsContent>
                    <TabsContent value="url" className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="doc-url">Document URL *</Label>
                        <Input
                          id="doc-url"
                          type="url"
                          placeholder="https://drive.google.com/file/d/..."
                          value={documentForm.url}
                          onChange={(e) =>
                            setDocumentForm({ ...documentForm, url: e.target.value })
                          }
                          required={uploadMethod === "url"}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Link to documents on Google Drive, Dropbox, or any external URL</p>
                      </div>
                    </TabsContent>
                  </Tabs>
                  <div>
                    <Label htmlFor="doc-title">Document Title *</Label>
                    <Input
                      id="doc-title"
                      value={documentForm.title}
                      onChange={(e) =>
                        setDocumentForm({ ...documentForm, title: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="doc-description">Description (optional)</Label>
                    <Input
                      id="doc-description"
                      value={documentForm.description}
                      onChange={(e) =>
                        setDocumentForm({ ...documentForm, description: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="folder-name">Folder Name (optional)</Label>
                    <Input
                      id="folder-name"
                      placeholder="e.g., Lecture Notes, Homework"
                      value={documentForm.folderName}
                      onChange={(e) =>
                        setDocumentForm({ ...documentForm, folderName: e.target.value })
                      }
                      list="existing-folders"
                    />
                    <datalist id="existing-folders">
                      {existingFolders.map(folder => (
                        <option key={folder} value={folder} />
                      ))}
                    </datalist>
                    <p className="text-xs text-muted-foreground mt-1">
                      Group related documents together. Leave empty for no folder.
                    </p>
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={isUploading}>
                      {isUploading ? "Adding..." : "Add Document"}
                    </Button>
                  </DialogFooter>
                </form>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="max-h-[500px]">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading documents...</p>
        ) : documents.length === 0 ? (
          <p className="text-sm text-muted-foreground">No documents uploaded yet</p>
        ) : (
          <ScrollArea className="h-[450px] pr-4">
            <div className="space-y-4">
              {/* Group documents by folder */}
              {(() => {
                const grouped = documents.reduce((acc, doc) => {
                  const folder = doc.folder_name || "General";
                  if (!acc[folder]) acc[folder] = [];
                  acc[folder].push(doc);
                  return acc;
                }, {} as Record<string, CourseDocument[]>);

                return Object.entries(grouped).map(([folder, folderDocs]) => (
                  <div key={folder} className="space-y-2">
                    <div className="font-semibold text-sm text-muted-foreground uppercase tracking-wide px-1">
                      📁 {folder}
                    </div>
                    {folderDocs.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors ml-4"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{doc.title}</div>
                            {doc.description && (
                              <div className="text-sm text-muted-foreground truncate">
                                {doc.description}
                              </div>
                            )}
                            {doc.file_size && (
                              <div className="text-xs text-muted-foreground">
                                {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <DocumentLink 
                            bucket="course-documents" 
                            filePath={doc.file_path}
                          >
                            <Button variant="ghost" size="sm">
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </DocumentLink>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(doc.id, doc.file_path)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};
